import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { Message, PaginatedResponse } from '../types';
import { apiService } from '../services/api';

interface MessageState {
  // State
  messages: Message[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  nextPage: string | null;
  totalCount: number;
  
  // WebSocket
  wsConnected: boolean;
  
  // Actions
  fetchMessages: (params?: {
    page?: number;
    direction?: 'inbound' | 'outbound';
    status?: string;
    phone?: string;
    start_date?: string;
    end_date?: string;
  }) => Promise<void>;
  
  loadMoreMessages: () => Promise<void>;
  refreshMessages: () => Promise<void>;
  
  addMessage: (message: Message) => void;
  updateMessage: (messageId: string, updates: Partial<Message>) => void;
  removeMessage: (messageId: string) => void;
  
  setWebSocketStatus: (connected: boolean) => void;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  messages: [],
  loading: false,
  error: null,
  hasMore: false,
  nextPage: null,
  totalCount: 0,
  wsConnected: false,
};

export const useMessageStore = create<MessageState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      fetchMessages: async (params = {}) => {
        set({ loading: true, error: null });
        
        try {
          const response: PaginatedResponse<Message> = await apiService.getMessages(params);
          
          set({
            messages: response.results,
            hasMore: !!response.next,
            nextPage: response.next,
            totalCount: response.count,
            loading: false,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch messages',
            loading: false,
          });
        }
      },

      loadMoreMessages: async () => {
        const { nextPage, loading, messages } = get();
        
        if (!nextPage || loading) return;

        set({ loading: true });

        try {
          // Extract page number from nextPage URL
          const url = new URL(nextPage);
          const page = url.searchParams.get('page');
          
          if (page) {
            const response: PaginatedResponse<Message> = await apiService.getMessages({ 
              page: parseInt(page, 10) 
            });
            
            set({
              messages: [...messages, ...response.results],
              hasMore: !!response.next,
              nextPage: response.next,
              totalCount: response.count,
              loading: false,
            });
          }
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to load more messages',
            loading: false,
          });
        }
      },

      refreshMessages: async () => {
        const { fetchMessages } = get();
        await fetchMessages();
      },

      addMessage: (message: Message) => {
        set((state) => ({
          messages: [message, ...state.messages],
          totalCount: state.totalCount + 1,
        }));
      },

      updateMessage: (messageId: string, updates: Partial<Message>) => {
        set((state) => ({
          messages: state.messages.map((msg) =>
            msg.id === messageId ? { ...msg, ...updates } : msg
          ),
        }));
      },

      removeMessage: (messageId: string) => {
        set((state) => ({
          messages: state.messages.filter((msg) => msg.id !== messageId),
          totalCount: Math.max(0, state.totalCount - 1),
        }));
      },

      setWebSocketStatus: (connected: boolean) => {
        set({ wsConnected: connected });
      },

      clearError: () => {
        set({ error: null });
      },

      reset: () => {
        set(initialState);
      },
    }),
    {
      name: 'message-store',
    }
  )
);